import type { SelectOption } from "@leabankab/component-library";
import type { CountryCode } from "@/config/country-config";
import { getCountryRules } from "@/config/country-rules";
import {
	RefinanceContext,
	RefinanceContextDebt,
} from "@/features/refinance/types";
import { toAmountOrZero } from "@/shared/amount";
import { matchOptionValue } from "./match-option-value";

/** Matches the `banks` option key whatever casing or spacing FLOW sends. */
const LEA_BANK_OPTION: SelectOption = { value: "leaBank", label: "Lea Bank" };

const isLeaBankName = (name: string | undefined): boolean =>
	matchOptionValue(name, [LEA_BANK_OPTION]) !== "";

/** FLOW's flag decides; the creditor name is the fallback for unflagged rows. */
export const isLeaBankDebt = (
	debt: Pick<RefinanceContextDebt, "bank" | "isLeaBankDebt">
): boolean => debt.isLeaBankDebt === true || isLeaBankName(debt.bank);

export const splitLeaBankDebts = (debts: RefinanceContextDebt[]) => ({
	leaBank: debts.filter(isLeaBankDebt),
	others: debts.filter((debt) => !isLeaBankDebt(debt)),
});

/** A customer cannot refinance a Lea Bank debt into a Lea Bank loan by hand. */
export const withoutLeaBank = <T extends { value: string }>(
	options: T[]
): T[] => options.filter((option) => !isLeaBankName(option.value));

/** Drops a top-up's existing loan where the new loan does not settle it. */
export const resolveRefinanceContextForCountry = (
	context: RefinanceContext,
	country: CountryCode
): RefinanceContext =>
	getCountryRules(country).settlesLeaBankLoan
		? context
		: {
				...context,
				offer: { ...context.offer, existingLoanCapital: undefined },
			};

export type LockedDebtReason = "topUp" | "notRefinanceable" | "existingLoan";

export interface LockedDebt {
	id: string;
	amount: number;
	/** A `loan-types` option key, or `""` when the credit type is unknown. */
	description: string;
	reason: LockedDebtReason;
}

interface LockedDebtsInput {
	leaBank: RefinanceContextDebt[];
	existingLoan: number;
	typeOptions: SelectOption[];
	country: CountryCode;
}

/**
 * The Lea Bank debts the customer is shown but cannot select, leaving out rows
 * with no balance. In a top-up the first row that is not a card is the loan
 * being settled, shown at `existingLoanCapital` because the credit check's
 * balance is not exact.
 */
export const resolveLockedDebts = ({
	leaBank,
	existingLoan,
	typeOptions,
	country,
}: LockedDebtsInput): LockedDebt[] => {
	const settles = getCountryRules(country).settlesLeaBankLoan;
	const rows: LockedDebt[] = leaBank.map((debt, index) => ({
		id: `lea-bank-${index}`,
		amount: toAmountOrZero(debt.amount),
		description:
			matchOptionValue(debt.description, typeOptions) ||
			matchOptionValue(debt.type, typeOptions),
		reason: settles ? "notRefinanceable" : "existingLoan",
	}));

	if (!settles || existingLoan <= 0) {
		return rows.filter((row) => row.amount > 0);
	}

	const topUpIndex = rows.findIndex((row) => row.description !== "card");

	return [
		{
			id: "existing-loan",
			amount: existingLoan,
			description: matchOptionValue("loan", typeOptions),
			reason: "topUp",
		},
		...rows.filter((row, index) => index !== topUpIndex && row.amount > 0),
	];
};
